import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, DollarSign, PieChart, BarChart3, Save, Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import roiCalculatorService from '../../services/roiCalculatorService';
import { propertyService } from '../../services/propertyService';
import { communeService } from '../../services/communeService';
import EmptyState from '../../components/ui/EmptyState';
import { trackROICalculation } from '../../services/userJourneyService';
import { gaTrackROICalculation } from '../../services/gaAnalyticsService';

const ROICalculatorTool = () => {
  // Form state
  const [formData, setFormData] = useState({
    propertyPrice: '',
    downPaymentPercentage: '20',
    loanInterestRate: '5.5',
    loanTermYears: '20',
    expectedRentalIncome: '',
    monthlyExpenses: '25000',
    annualAppreciationRate: '3.5',
    propertyTaxRate: '1.2',
    insuranceCost: '0',
    maintenanceCost: '15000'
  });

  // Results and UI state
  const [roiResults, setRoiResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [properties, setProperties] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [calculationName, setCalculationName] = useState('');
  const [savedCalculations, setSavedCalculations] = useState([]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [propertiesData, communesData] = await Promise.all([
        propertyService?.getAllProperties(),
        communeService?.getAllCommunes()
      ]);
      
      // Handle the response format from services
      setProperties(propertiesData?.data || []);
      setCommunes(communesData?.data || []);
    } catch (error) {
      setError('Erreur lors du chargement des données initiales');
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  // Handle property selection for one-click calculation
  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setFormData(prev => ({
      ...prev,
      propertyPrice: property?.price?.toString() || '',
      expectedRentalIncome: calculateEstimatedRental(property),
      monthlyExpenses: getDefaultExpensesByCommune(property?.commune?.name)
    }));
    setCalculationName(`Analyse ROI - ${property?.title || 'Propriété'}`);
  };

  // Calculate estimated rental based on property and commune data
  const calculateEstimatedRental = (property) => {
    if (!property?.commune?.average_price || !property?.surface_area) return '';
    
    const avgPricePerSqm = property?.commune?.average_price;
    const estimatedMonthlyRent = (avgPricePerSqm * property?.surface_area * 0.008)?.toFixed(0);
    return estimatedMonthlyRent;
  };

  // Get default expenses by commune
  const getDefaultExpensesByCommune = (communeName) => {
    const expensesByCommune = {
      'Cocody': '35000',
      'Plateau': '40000',
      'Marcory': '30000',
      'Yopougon': '25000'
    };
    return expensesByCommune?.[communeName] || '25000';
  };

  // Calculate ROI
  const calculateROI = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Validate required fields
      if (!formData?.propertyPrice || !formData?.downPaymentPercentage) {
        setError('Veuillez renseigner au minimum le prix de la propriété et l\'apport initial');
        setLoading(false);
        return;
      }

      const calculationData = {
        propertyPrice: parseFloat(formData?.propertyPrice),
        downPaymentPercentage: parseFloat(formData?.downPaymentPercentage),
        loanInterestRate: parseFloat(formData?.loanInterestRate),
        loanTermYears: parseInt(formData?.loanTermYears),
        expectedRentalIncome: parseFloat(formData?.expectedRentalIncome) || 0,
        monthlyExpenses: parseFloat(formData?.monthlyExpenses) || 0,
        annualAppreciationRate: parseFloat(formData?.annualAppreciationRate) || 0
      };

      const results = await roiCalculatorService?.calculateROI(calculationData);
      
      if (results) {
        setRoiResults(results);
        setSuccess('Calcul ROI effectué avec succès !');

        // Track ROI calculation event
        trackROICalculation({
          propertyId: selectedProperty?.id || null,
          propertyPrice: calculationData?.propertyPrice,
          totalROI: results?.total_roi,
          capRate: results?.cap_rate,
          cashOnCashReturn: results?.cash_on_cash_return,
        });
        gaTrackROICalculation({
          propertyId: selectedProperty?.id || null,
          propertyPrice: calculationData?.propertyPrice,
          totalROI: results?.total_roi,
          capRate: results?.cap_rate,
          cashOnCashReturn: results?.cash_on_cash_return,
        });
      }
    } catch (error) {
      setError(error?.message || 'Erreur lors du calcul ROI');
    } finally {
      setLoading(false);
    }
  };

  // Save calculation
  const saveCalculation = async () => {
    if (!roiResults || !calculationName?.trim()) {
      setError('Veuillez effectuer un calcul et donner un nom à votre analyse');
      return;
    }

    setLoading(true);
    try {
      const calculationData = {
        calculationName: calculationName?.trim(),
        propertyId: selectedProperty?.id || null,
        propertyPrice: parseFloat(formData?.propertyPrice),
        downPaymentPercentage: parseFloat(formData?.downPaymentPercentage),
        loanAmount: roiResults?.loan_amount,
        loanInterestRate: parseFloat(formData?.loanInterestRate),
        loanTermYears: parseInt(formData?.loanTermYears),
        expectedRentalIncome: parseFloat(formData?.expectedRentalIncome) || null,
        monthlyExpenses: parseFloat(formData?.monthlyExpenses) || 0,
        annualAppreciationRate: parseFloat(formData?.annualAppreciationRate) || 0,
        propertyTaxRate: parseFloat(formData?.propertyTaxRate) || 0,
        insuranceCost: parseFloat(formData?.insuranceCost) || 0,
        maintenanceCost: parseFloat(formData?.maintenanceCost) || 0,
        cashOnCashReturn: roiResults?.cash_on_cash_return,
        capRate: roiResults?.cap_rate,
        totalROI: roiResults?.total_roi,
        breakEvenMonths: roiResults?.break_even_months,
        status: 'saved',
        userId: 'temp-user-id' // This would come from auth context
      };

      await roiCalculatorService?.saveCalculation(calculationData);
      setSuccess('Calcul sauvegardé avec succès !');
      setCalculationName('');
    } catch (error) {
      setError(error?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Generate 5, 10, 15 year projections
  const generateProjections = () => {
    if (!roiResults) return [];
    
    const years = [5, 10, 15];
    const projections = years?.map(year => {
      const propertyValue = parseFloat(formData?.propertyPrice) * Math.pow(1 + (parseFloat(formData?.annualAppreciationRate) / 100), year);
      const totalCashFlow = roiResults?.annual_cash_flow * year;
      const totalReturn = propertyValue - parseFloat(formData?.propertyPrice) + totalCashFlow;
      
      return {
        year,
        propertyValue: propertyValue?.toFixed(0),
        totalCashFlow: totalCashFlow?.toFixed(0),
        totalReturn: totalReturn?.toFixed(0),
        roi: ((totalReturn / (parseFloat(formData?.propertyPrice) * parseFloat(formData?.downPaymentPercentage) / 100)) * 100)?.toFixed(1)
      };
    });
    
    return projections;
  };

  const projections = generateProjections();

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Calculator className="h-12 w-12 text-brand-gold mr-3" />
            <h1 className="text-3xl font-display font-display-semibold text-foreground">Calculateur ROI Immobilier</h1>
          </div>
          <p className="text-base text-muted-foreground max-w-3xl mx-auto">
            Analysez la rentabilité de vos investissements immobiliers à Abidjan avec des calculs précis basés sur les données du marché local
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Input Parameters */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <DollarSign className="h-6 w-6 mr-2 text-blue-600" />
              Paramètres d'Investissement
            </h2>

            {/* Property Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner une propriété (optionnel)
              </label>
              <select
                value={selectedProperty?.id || ''}
                onChange={(e) => {
                  const property = properties?.find(p => p?.id === e?.target?.value);
                  if (property) handlePropertySelect(property);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Calcul personnalisé</option>
                {properties?.map(property => (
                  <option key={property?.id} value={property?.id}>
                    {property?.title} - {property?.commune?.name} - {property?.price?.toLocaleString()} FCFA
                  </option>
                ))}
              </select>
            </div>

            {/* Investment Parameters */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Prix de la propriété (FCFA)"
                  name="propertyPrice"
                  type="number"
                  value={formData?.propertyPrice}
                  onChange={handleInputChange}
                  placeholder="Ex: 25000000"
                  required
                />
                <Input
                  label="Apport initial (%)"
                  name="downPaymentPercentage"
                  type="number"
                  value={formData?.downPaymentPercentage}
                  onChange={handleInputChange}
                  placeholder="Ex: 20"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Taux d'intérêt crédit (%)"
                  name="loanInterestRate"
                  type="number"
                  step="0.1"
                  value={formData?.loanInterestRate}
                  onChange={handleInputChange}
                  placeholder="Ex: 5.5"
                />
                <Input
                  label="Durée du crédit (années)"
                  name="loanTermYears"
                  type="number"
                  value={formData?.loanTermYears}
                  onChange={handleInputChange}
                  placeholder="Ex: 20"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Revenus locatifs mensuels (FCFA)"
                  name="expectedRentalIncome"
                  type="number"
                  value={formData?.expectedRentalIncome}
                  onChange={handleInputChange}
                  placeholder="Ex: 180000"
                />
                <Input
                  label="Charges mensuelles (FCFA)"
                  name="monthlyExpenses"
                  type="number"
                  value={formData?.monthlyExpenses}
                  onChange={handleInputChange}
                  placeholder="Ex: 25000"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Appréciation annuelle (%)"
                  name="annualAppreciationRate"
                  type="number"
                  step="0.1"
                  value={formData?.annualAppreciationRate}
                  onChange={handleInputChange}
                  placeholder="Ex: 3.5"
                />
                <Input
                  label="Taxe foncière (%)"
                  name="propertyTaxRate"
                  type="number"
                  step="0.1"
                  value={formData?.propertyTaxRate}
                  onChange={handleInputChange}
                  placeholder="Ex: 1.2"
                />
              </div>
            </div>

            {/* Calculate Button */}
            <div className="mt-6">
              <Button
                onClick={calculateROI}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
              >
                {loading ? 'Calcul en cours...' : 'Calculer le ROI'}
              </Button>
            </div>
          </div>

          {/* Right Panel - Results */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="h-6 w-6 mr-2 text-green-600" />
              Résultats de l'Analyse
            </h2>

            {roiResults ? (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-blue-600 font-medium">Cash-on-Cash Return</div>
                    <div className="text-2xl font-bold text-blue-900">{roiResults?.cash_on_cash_return?.toFixed(1)}%</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-sm text-green-600 font-medium">Cap Rate</div>
                    <div className="text-2xl font-bold text-green-900">{roiResults?.cap_rate?.toFixed(1)}%</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm text-purple-600 font-medium">ROI Total</div>
                    <div className="text-2xl font-bold text-purple-900">{roiResults?.total_roi?.toFixed(1)}%</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="text-sm text-orange-600 font-medium">Seuil de Rentabilité</div>
                    <div className="text-2xl font-bold text-orange-900">
                      {roiResults?.break_even_months ? `${roiResults?.break_even_months} mois` : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Financial Details */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <PieChart className="h-5 w-5 mr-2" />
                    Détails Financiers
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Apport initial:</span>
                      <span className="font-medium">{roiResults?.down_payment?.toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Montant emprunté:</span>
                      <span className="font-medium">{roiResults?.loan_amount?.toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Mensualité crédit:</span>
                      <span className="font-medium">{roiResults?.monthly_payment?.toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Cash-flow mensuel:</span>
                      <span className={`font-medium ${roiResults?.monthly_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {roiResults?.monthly_cash_flow?.toLocaleString()} FCFA
                      </span>
                    </div>
                  </div>
                </div>

                {/* Projections */}
                {projections?.length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2" />
                      Projections
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Année</th>
                            <th className="text-right py-2">Valeur Bien</th>
                            <th className="text-right py-2">Cash-flow Total</th>
                            <th className="text-right py-2">ROI Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projections?.map(projection => (
                            <tr key={projection?.year} className="border-b">
                              <td className="py-2 font-medium">{projection?.year} ans</td>
                              <td className="text-right py-2">{parseInt(projection?.propertyValue)?.toLocaleString()} FCFA</td>
                              <td className="text-right py-2">{parseInt(projection?.totalCashFlow)?.toLocaleString()} FCFA</td>
                              <td className="text-right py-2 font-medium text-green-600">{projection?.roi}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Save Calculation */}
                <div className="border-t pt-6">
                  <div className="space-y-3">
                    <Input
                      label="Nom de l'analyse (pour sauvegarde)"
                      value={calculationName}
                      onChange={(e) => setCalculationName(e?.target?.value)}
                      placeholder="Ex: Villa Cocody - Analyse ROI"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Button
                        onClick={saveCalculation}
                        disabled={loading || !calculationName?.trim()}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </Button>
                      <Button
                        onClick={() => window.print()}
                        className="bg-gray-600 hover:bg-gray-700 text-white"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Exporter PDF
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState
                iconName="Calculator"
                title="Aucun résultat à afficher"
                description="Remplissez les paramètres d'investissement et cliquez sur « Calculer le ROI » pour voir les résultats."
                size="md"
              />
            )}
          </div>
        </div>

        {/* Integration with Property Listings */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Analyse Rapide - Propriétés Disponibles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties?.length > 0 ? (
              properties?.slice(0, 6)?.map(property => (
                <div key={property?.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">{property?.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{property?.commune?.name}</p>
                  <p className="text-lg font-bold text-blue-600 mb-3">{property?.price?.toLocaleString()} FCFA</p>
                  <Button
                    onClick={() => handlePropertySelect(property)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  >
                    Calculer ROI
                  </Button>
                </div>
              ))
            ) : (
              <div className="col-span-3">
                <EmptyState
                  iconName="Building2"
                  title="Aucune propriété disponible"
                  description="Les propriétés disponibles pour l'analyse rapide apparaîtront ici."
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculatorTool;